import { appStorage } from './betaStorage'
const PROFILE_PHOTO_PREFIX = 'schub.profilePhoto.'
const PROFILE_PHOTO_OVERRIDE_VERSION_PREFIX = 'schub.profilePhotoOverrideVersion.'
const PROFILE_PHOTO_SCHEMA_KEY = 'schub.profilePhotoSchema'
const PROFILE_PHOTO_SCHEMA_VERSION = '7'
const PROFILE_PHOTO_OVERRIDE_VERSION = '5'
export const PROFILE_PHOTO_CHANGED = 'schub-profile-photo-changed'

// Canonical prototype portraits. These are the approved demo photos used when
// no photo has been uploaded through the CURRENT profile editor build.
// Older browser tabs are deliberately prevented from resurrecting stale photos.
const DEMO_PROFILE_PHOTOS: Record<string, string> = {
  'u-alex': 'https://unsplash.com/photos/gRslJX1S4h0/download?force=true&w=640',
  'u-mark': 'https://unsplash.com/photos/_tcJW4nqVtw/download?force=true&w=640',
  'u-jess': 'https://unsplash.com/photos/cdZcNIca4w0/download?force=true&w=480',
  'u-priya': 'https://unsplash.com/photos/nt_oOuRObNY/download?force=true&w=480',
  'u-dan': 'https://unsplash.com/photos/sgYGdrslz50/download?force=true&w=480',
  'u-amy': 'https://unsplash.com/photos/TSZo17r3m0s/download?force=true&w=480',
  'u-sarah': 'https://unsplash.com/photos/eBR7JFPr5q8/download?force=true&w=480',
  'u-tom': 'https://unsplash.com/photos/VBU3WW_Japk/download?force=true&w=480',
  'u-casey': 'https://unsplash.com/photos/G6Jf-T3Z6IQ/download?force=true&w=480',
  'u-leo': 'https://unsplash.com/photos/_LMbIJ7_0Ac/download?force=true&w=480',
  'u-jnr': 'https://unsplash.com/photos/AS-WFs2Y8pA/download?force=true&w=480',
  'u-lillian': 'https://unsplash.com/photos/sdRi6boBozA/download?force=true&w=480',
  'u-mia': 'https://unsplash.com/photos/QLgx1tloL0E/download?force=true&w=480',
  'u-jordan': 'https://unsplash.com/photos/nX0mSJ999Og/download?force=true&w=480',
  'u-olivia': 'https://unsplash.com/photos/WjR1Eyho9Cs/download?force=true&w=480',
}

function photoKey(userId: string) {
  return `${PROFILE_PHOTO_PREFIX}${userId}`
}

function overrideVersionKey(userId: string) {
  return `${PROFILE_PHOTO_OVERRIDE_VERSION_PREFIX}${userId}`
}

function migrateProfilePhotoMetadata() {
  try {
    if (appStorage.getItem(PROFILE_PHOTO_SCHEMA_KEY) === PROFILE_PHOTO_SCHEMA_VERSION) return

    Object.keys(DEMO_PROFILE_PHOTOS).forEach((userId) => {
      appStorage.removeItem(overrideVersionKey(userId))
    })
    appStorage.setItem(PROFILE_PHOTO_SCHEMA_KEY, PROFILE_PHOTO_SCHEMA_VERSION)
  } catch {
    // appStorage can be unavailable in private/restricted browser contexts.
  }
}

export function profilePhotoFor(userId: string): string | null {
  migrateProfilePhotoMetadata()
  try {
    const savedPhoto = appStorage.getItem(photoKey(userId))
    const savedVersion = appStorage.getItem(overrideVersionKey(userId))
    if (savedPhoto && savedVersion === PROFILE_PHOTO_OVERRIDE_VERSION) return savedPhoto
    return DEMO_PROFILE_PHOTOS[userId] ?? null
  } catch {
    return DEMO_PROFILE_PHOTOS[userId] ?? null
  }
}

export function saveProfilePhoto(userId: string, dataUrl: string) {
  migrateProfilePhotoMetadata()
  appStorage.setItem(photoKey(userId), dataUrl)
  appStorage.setItem(overrideVersionKey(userId), PROFILE_PHOTO_OVERRIDE_VERSION)
  window.dispatchEvent(new CustomEvent(PROFILE_PHOTO_CHANGED, { detail: { userId } }))
}

export function removeProfilePhoto(userId: string) {
  migrateProfilePhotoMetadata()
  appStorage.removeItem(photoKey(userId))
  appStorage.removeItem(overrideVersionKey(userId))
  window.dispatchEvent(new CustomEvent(PROFILE_PHOTO_CHANGED, { detail: { userId } }))
}

export function resizeProfilePhoto(file: File, maxSize = 512): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please choose an image file.'))
      return
    }

    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read that image.'))
    reader.onload = () => {
      const image = new Image()
      image.onerror = () => reject(new Error('Could not open that image.'))
      image.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(image.width, image.height))
        const width = Math.max(1, Math.round(image.width * scale))
        const height = Math.max(1, Math.round(image.height * scale))
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Could not prepare that image.'))
          return
        }
        ctx.drawImage(image, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', 0.82))
      }
      image.src = String(reader.result)
    }
    reader.readAsDataURL(file)
  })
}

